/**
 * Axios Plugin implementing secured and plain axios requests
 */

import axios from 'axios';
import { processError } from '../api/errorHandler';
import router from '../router';
import { useAppStore } from '@store/app';
import { useUserStore } from '@store/user';
import { newIdempotencyKey } from '@/utilities/idempotencyKey';

const BASE_API_URL = import.meta.env.VITE_API_BASE_URL;

const centralAxiosInstance = axios.create({
    baseURL: `${BASE_API_URL}/api/v1`,
    headers: {
        'X-Request-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
    },
    withCredentials: true,
    withXSRFToken: true,
});

const tenantAxiosInstance = axios.create({
    baseURL: `${BASE_API_URL}/-/api/v1`,
    headers: {
        'X-Request-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
    },
    withCredentials: true,
    withXSRFToken: true,
});

// Dedicated axios instance for the passwordless crew task link page.
// Uses sessionStorage (per-tab) so it never clobbers a logged-in staff
// member's `bearer`/`tenantSlug` in localStorage when both are open.
const crewAxiosInstance = axios.create({
    baseURL: `${BASE_API_URL}/-/api/v1`,
    headers: {
        'X-Request-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
    },
    withCredentials: true,
    withXSRFToken: true,
});

centralAxiosInstance.interceptors.request.use((config) => {
    // Get local storage token in native javascript
    let token = localStorage.getItem('bearer');

    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Attach persistent device ID for login anomaly detection
    let deviceId = localStorage.getItem('device_id');
    if (deviceId) {
        config.headers['X-Device-Id'] = deviceId;
    }

    // Abort logic setup for axios
    const app = useAppStore();

    // Get router currentRoute path name
    const currentRoute = router.currentRoute.value.path;

    if (app.axiosAbortControllerList[currentRoute]) {
        config.signal = app.axiosAbortControllerList[currentRoute].controller.signal;
    } else {
        // Add to axiosAbortControllerList with key of currentRoute
        app.axiosAbortControllerList[currentRoute] = {
            name: currentRoute,
            controller: new AbortController(),
        };
        config.signal = app.axiosAbortControllerList[currentRoute].controller.signal;
    }

    return config;
});

tenantAxiosInstance.interceptors.request.use((config) => {
    // Get local storage token in native javascript
    let token = localStorage.getItem('bearer');

    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Attach persistent device ID for login anomaly detection
    let deviceId = localStorage.getItem('device_id');
    if (deviceId) {
        config.headers['X-Device-Id'] = deviceId;
    }

    // Get local storage tenant in native javascript
    let tenantSlug = localStorage.getItem('tenantSlug');

    if (tenantSlug) {
        config.baseURL = `${BASE_API_URL}/${tenantSlug}/api/v1`;
    } else {
        config.baseURL = `${BASE_API_URL}/-/api/v1`;
    }

    // Auto-attach an Idempotency-Key to every write request that does not
    // already have one. Callers that generate their own key (e.g. form submit
    // handlers that want retry-collapsing) pass it explicitly and it is
    // preserved. All other write calls (resource-assignment modals, swimlane
    // actions, etc.) get a fresh per-request key so the EnforceIdempotency
    // middleware is satisfied without duplicating diary rows.
    const writeMethods = ['post', 'put', 'patch', 'delete'];
    if (writeMethods.includes((config.method ?? '').toLowerCase())) {
        if (!config.headers['Idempotency-Key']) {
            config.headers['Idempotency-Key'] = newIdempotencyKey();
        }
    }

    // Abort logic setup for axios
    const app = useAppStore();

    // Get router currentRoute path name
    const currentRoute = router.currentRoute.value.path;

    if (app.axiosAbortControllerList[currentRoute]) {
        config.signal = app.axiosAbortControllerList[currentRoute].controller.signal;
    } else {
        // Add to axiosAbortControllerList with key of currentRoute
        app.axiosAbortControllerList[currentRoute] = {
            name: currentRoute,
            controller: new AbortController(),
        };
        config.signal = app.axiosAbortControllerList[currentRoute].controller.signal;
    }

    return config;
});

const createDynamicTenantAxiosInstance = (tenantSlug) => {
    const dynamicTenantAxiosInstance = axios.create({
        baseURL: tenantSlug ? `${BASE_API_URL}/${tenantSlug}/api/v1` : `${BASE_API_URL}/-/api/v1`,
        headers: {
            'X-Request-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
        },
        withCredentials: true,
        withXSRFToken: true,
    });

    dynamicTenantAxiosInstance.interceptors.request.use((config) => {
        // Get local storage token in native javascript
        let token = localStorage.getItem('bearer');

        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        // Attach persistent device ID for login anomaly detection
        let deviceId = localStorage.getItem('device_id');
        if (deviceId) {
            config.headers['X-Device-Id'] = deviceId;
        }

        // Abort logic setup for axios
        const app = useAppStore();

        // Get router currentRoute path name
        const currentRoute = router.currentRoute.value.path;

        if (app.axiosAbortControllerList[currentRoute]) {
            config.signal = app.axiosAbortControllerList[currentRoute].controller.signal;
        } else {
            // Add to axiosAbortControllerList with key of currentRoute
            app.axiosAbortControllerList[currentRoute] = {
                name: currentRoute,
                controller: new AbortController(),
            };
            config.signal = app.axiosAbortControllerList[currentRoute].controller.signal;
        }

        return config;
    });

    dynamicTenantAxiosInstance.interceptors.response.use(null, axiosResponseInterceptor);

    return dynamicTenantAxiosInstance;
};

const axiosResponseInterceptor = (error) => {
    const userStore = useUserStore();
    const processedError = processError(error);

    if (error?.response?.status === 401) {
        router.isReady().then(() => {
            if (router.currentRoute.value.meta.requiresAuth) {
                userStore.$reset();
                router.replace({ name: 'Login' });
            }
        });
    }

    // MFA setup required — redirect to setup page regardless of auth state
    if (error?.response?.status === 403 && error?.response?.data?.mfa_setup_required) {
        router.push({ name: 'MfaSetup' });
        return Promise.reject(processedError);
    }

    if (error?.config?.xSilent === true) {
        console.log('Skipped route redirect as request was set to silent', error);
    } else {
        if (error?.response?.status === 403 && userStore.isAuthenticated) {
            router.push({ name: '403Page' });
        }

        if (error?.response?.status === 503) {
            router.push({ name: '503Page' });
        }
    }

    return Promise.reject(processedError);
};

crewAxiosInstance.interceptors.request.use((config) => {
    // Read crew bearer + slug from sessionStorage (per-tab isolation).
    const token = sessionStorage.getItem('crew_bearer');
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }

    const tenantSlug = sessionStorage.getItem('crew_tenantSlug');
    if (tenantSlug) {
        config.baseURL = `${BASE_API_URL}/${tenantSlug}/api/v1`;
    } else {
        config.baseURL = `${BASE_API_URL}/-/api/v1`;
    }

    return config;
});

centralAxiosInstance.interceptors.response.use(null, axiosResponseInterceptor);
tenantAxiosInstance.interceptors.response.use(null, axiosResponseInterceptor);
crewAxiosInstance.interceptors.response.use(null, axiosResponseInterceptor);

/*
 * Install global and Vue alias
 */

// Make instance available globally as axios()
window.centralAxios = centralAxiosInstance;
window.tenantAxios = tenantAxiosInstance;
window.crewAxios = crewAxiosInstance;

export default {
    install: (app /*options*/) => {
        // Make instance available as instance property this.$axios()
        app.config.globalProperties.$centralAxios = centralAxiosInstance;
        app.config.globalProperties.$tenantAxios = tenantAxiosInstance;
        app.config.globalProperties.$crewAxios = crewAxiosInstance;
    },
    createDynamicTenantAxiosInstance,
};
